// Pure rules for Explore & Craft. Kept separate so saves and building can be tested without WebGL.
import { B } from "./blocks.js";
import { BUILD_ITEMS, addItem, amount, blockItem } from "./crafting.js";
import { giveOrKeep } from "./expansion-rules.js";
import {
  groundHeight,
  homeRegion,
  localCoord,
  REGION,
} from "./discovery-terrain.js";

export const STATIONS = [
  {
    id: "numbers",
    name: "Number Grove",
    icon: "🌳",
    colour: "#7be35a",
    x: -12,
    z: 0,
    topics: { add: 1, sub: 1 },
    tip: "Collect number magic. Split big numbers into tens and ones.",
  },
  {
    id: "words",
    name: "Word Cottage",
    icon: "📚",
    colour: "#ff9abb",
    x: -11,
    z: 13,
    topics: { spell: 2, word: 1 },
    tip: "Read, spell and imagine. Say tricky words slowly.",
  },
  {
    id: "crystals",
    name: "Crystal Cavern",
    icon: "💎",
    colour: "#bd8bff",
    x: 0,
    z: 19,
    topics: { pattern: 1, place: 1 },
    tip: "Look for patterns. What changes each time?",
  },
  {
    id: "farm",
    name: "Fraction Farm",
    icon: "🌻",
    colour: "#ffd23f",
    x: 12,
    z: 12,
    topics: { fraction: 1, div: 1 },
    tip: "Share fairly. Every equal part must be the same size.",
  },
  {
    id: "clock",
    name: "Clock Tower",
    icon: "🕒",
    colour: "#6fd5ff",
    x: 13,
    z: -1,
    topics: { time: 1, mul: 1 },
    tip: "The long hand counts minutes. Each number is five minutes.",
  },
  {
    id: "builder",
    name: "Builder Workshop",
    icon: "🧱",
    colour: "#ffac6a",
    x: 0,
    z: -12,
    topics: { shapes: 1, missing: 1 },
    tip: "Find shapes and missing numbers. Use the opposite operation.",
  },
];
export const PALETTE = BUILD_ITEMS.map(([item, name, id]) => ({
  item,
  name,
  id,
}));
export const PLOT = { x0: -7, x1: 7, z0: 3, z1: 12, y0: 41, y1: 48 };
// The public crafting table: the Craft & Trade stall beside the pond, well away from every learning gem.
export const STALL = { x: -10, y: 41, z: -16 };
const stallCell = (x, z) => Math.hypot(x - STALL.x, z - STALL.z) < 1.8;
export const MAX_BLOCKS = 2048;
export function freshDiscovery() {
  return {
    answers: 0,
    stamps: {},
    blocks: [],
    supplies: 24,
    dragonHatched: false,
    visits: 0,
    built: false,
    biomes: {},
    guideDone: false,
    inventory: null,
    crafted: {},
    gathered: {},
    projects: {},
    harvested: [],
    hunted: {},
    worldTime: 0,
    hearts: 5,
    removed: [],
    defeated: {},
    // Shared worlds (1.5): edits waiting for the server (and blocks taken down while that queue was full),
    // and whether this world is on the server yet.
    pending: [],
    takenDown: [],
    worldSeeded: false,
    worldEpoch: null,
    teamMilestones: {},
  };
}
// Only the marked home grove renews. Demolition elsewhere stays saved.
export function renewableCell(x, y, z) {
  return x >= 7 && x <= 14 && z >= -24 && z <= -10 && y > 40;
}
// Spawn, lessons and the public crafting table stay clear; the rest of town is editable.
export function protectedCell(x, z) {
  return (
    Math.hypot(x - 0.5, z + 3.5) < 2.5 ||
    STATIONS.some((s) => Math.hypot(x - s.x, z - s.z) < 2.5) ||
    stallCell(x, z) || // public Craft & Trade stall
    (!homeRegion(Math.floor(x / REGION), Math.floor(z / REGION)) &&
      Math.abs(localCoord(x) - 16) <= 2 &&
      Math.abs(localCoord(z) - 16) <= 2)
  );
}
export function validBuildCell(x, y, z) {
  if (
    ![x, y, z].every(Number.isInteger) ||
    Math.abs(x) > 10000000 ||
    Math.abs(z) > 10000000 ||
    protectedCell(x, z)
  )
    return false;
  const ground = groundHeight(x, z);
  return y >= 9 && y <= 96;
}
export function cleanBlocks(blocks) {
  const cells = new Map();
  for (const b of Array.isArray(blocks) ? blocks : []) {
    if (
      Array.isArray(b) &&
      validBuildCell(...b.slice(0, 3)) &&
      PALETTE.some((p) => p.id === b[3])
    )
      cells.set(b.slice(0, 3).join(","), b.slice(0, 4));
    if (cells.size >= MAX_BLOCKS) break;
  }
  return [...cells.values()];
}
// The public table moved to the stall in 1.6, and cleanBlocks drops blocks saved in its clear space. Take them
// down first, as Remove would: yours go back in your bag, a friend's give no item, and a chest returns what it
// holds. `owners` is ExploreMode.hostOwners (friends' cells); `record` gets one remove op per cell so the shared
// world drops it too. Returns {removed, refunded}.
export function refundStallBlocks(d, owners = {}, record = () => {}) {
  const blocks = Array.isArray(d.blocks) ? d.blocks : [],
    found = new Map();
  for (const b of blocks)
    if (
      Array.isArray(b) &&
      b.slice(0, 4).every(Number.isInteger) &&
      b[1] >= 9 &&
      b[1] <= 96 &&
      stallCell(b[0], b[2]) &&
      PALETTE.some((p) => p.id === b[3])
    )
      found.set(b.slice(0, 3).join(","), b);
  let refunded = 0,
    waiting = 0;
  for (const [key, b] of found) {
    const [x, y, z, id] = b,
      prevOwner = owners[key] ?? null,
      item = blockItem(id),
      back = prevOwner === null && addItem(d, item, 1);
    if (back) refunded++;
    // A full stack never loses anything: the rest waits in Lost & found until there is room.
    else if (prevOwner === null) waiting += 1 - giveOrKeep(d, item, 1);
    const dev = d.expansion?.devices?.[key];
    if (dev) {
      for (const [kept, n] of Object.entries(dev.stash || {})) waiting += n - giveOrKeep(d, kept, n);
      delete d.expansion.devices[key];
    }
    delete owners[key];
    record({ k: "remove", x, y, z, id, item: back ? item : null, n: back ? 1 : 0, prevOwner });
  }
  let w = 0;
  for (const b of blocks)
    if (!(Array.isArray(b) && found.has(b.slice(0, 3).join(",")))) blocks[w++] = b;
  blocks.length = w;
  return { removed: found.size, refunded, waiting };
}
// Lesson chests (1.7), on top of the base rewards: one per finished lesson, themed by the lesson's subject.
// Everything inside is a gatherable resource, so a chest can never be sold back for currency.
export const LESSON_CHEST = {
  numbers: { common: { stone: 4 }, rare: { iron: 2 }, epic: { diamond: 1 } },
  words: { common: { wool: 2 }, rare: { food: 2 }, epic: { crystal: 2 } },
  crystals: { common: { crystal: 1 }, rare: { crystal: 3 }, epic: { diamond: 1 } },
  farm: { common: { seeds: 3 }, rare: { wheat: 4 }, epic: { egg: 1 } },
  clock: { common: { coal: 2 }, rare: { iron: 2 }, epic: { crystal: 3 } },
  builder: { common: { arrow: 6 }, rare: { wood: 4, stone: 4 }, epic: { diamond: 1 } },
};
export const CHEST_ODDS = { common: 0.7, rare: 0.25, epic: 0.05 };
const CHEST_TIERS = ["common", "rare", "epic"];
// Rolls the chest for a finished lesson (three right answers, not left with Later): {tier, items}, or null.
// `rand` returns [0, 1): the game passes its RNG, tests pass fixed values. A perfect lesson is one tier better.
export function rollLessonChest(stationId, result, rand = Math.random) {
  const table = LESSON_CHEST[stationId];
  if (!table || !result || result.cancelled || Math.floor(result.correct || 0) < 3)
    return null;
  const roll = rand();
  let tier = roll < CHEST_ODDS.common ? 0 : roll < CHEST_ODDS.common + CHEST_ODDS.rare ? 1 : 2;
  if (result.perfect) tier = Math.min(2, tier + 1);
  return { tier: CHEST_TIERS[tier], items: { ...table[CHEST_TIERS[tier]] } };
}
// The Builder's gift: the first Builder Workshop stamp brings a bow and 12 arrows, once, to a save with a
// backpack and no bow yet (the bow recipe stays for spares). The flag also marks saves that already had a bow.
// The bow takes the first free hotbar slot, as crafting does. Returns true when it was given now.
export const BUILDER_GIFT = { bow: 1, arrow: 12 };
export function builderGift(d) {
  if (!d.inventory || !d.stamps?.builder) return false;
  const ex = d.expansion && typeof d.expansion === "object" ? d.expansion : (d.expansion = {}),
    flags = ex.flags && typeof ex.flags === "object" ? ex.flags : (ex.flags = {});
  if (flags.bowGift) return false;
  flags.bowGift = true;
  if (amount(d, "bow")) return false;
  addItem(d, "bow", BUILDER_GIFT.bow);
  // A full arrow stack can't use up the one-time gift: what doesn't fit waits in Lost & found.
  giveOrKeep(d, "arrow", BUILDER_GIFT.arrow);
  const bar = d.inventory.hotbar, free = bar.indexOf(null);
  if (free >= 0 && !bar.includes("bow")) bar[free] = "bow";
  return true;
}
// result: the quiz result {correct, cancelled, xp, challengeWon}. Partial lessons pay their answers only.
export function discoveryReward(state, stationId, result) {
  if (!STATIONS.some((s) => s.id === stationId))
    throw new Error("Unknown discovery station");
  const count = Math.max(0, Math.min(3, Math.floor(result.correct || 0)));
  state.answers += count;
  if (state.inventory) addItem(state, "planks", count * 8);
  else state.supplies = Math.min(999, state.supplies + count * 8);
  const completed = count === 3 && !result.cancelled;
  const newStamp = completed && !state.stamps[stationId];
  if (completed) state.stamps[stationId] = (state.stamps[stationId] || 0) + 1;
  const hatch =
    !state.dragonHatched &&
    state.answers >= 18 &&
    STATIONS.every((s) => state.stamps[s.id]);
  if (hatch) state.dragonHatched = true;
  // A won 🔥 challenge adds a gem to a finished lesson.
  const challengeGem = completed && result.challengeWon ? 1 : 0;
  const gift = newStamp && stationId === "builder" && builderGift(state);
  return {
    count,
    completed,
    newStamp,
    hatch,
    coins: count * 5 + (newStamp ? 20 : 0),
    gems: completed ? (newStamp ? 2 : 1) + challengeGem : 0,
    challengeGem,
    gift,
    xp: Math.max(0, result.xp || 0),
  };
}
